
import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';

export interface MediaUrlsHandlerProps {
  mediaUrls: string[];
  setMediaUrls?: (urls: string[]) => void;
  name?: string;
  productId?: string;
  shopId?: string;
}

export interface MediaUrlsHandler {
  previewMediaUrls: string[];
  addMediaUrl: (url: string) => void;
  removeMediaUrl: (index: number) => void;
  cleanEmptyUrls: () => string[];
  commitPreviewUrls: () => string[];
  commitAndUpdateUrls?: () => void; // Add this optional method to the interface
}

export const useMediaUrlsHandler = (
  initialMediaUrls: string[] = [],
  shopId?: string,
  productId?: string,
  organizationId?: string
): MediaUrlsHandler => {
  console.log("useMediaUrlsHandler initialized with:", { 
    initialUrlsCount: initialMediaUrls?.length || 0,
    shopId, 
    productId,
    organizationId
  });
  
  const [previewMediaUrls, setPreviewMediaUrls] = useState<string[]>([]);
  const { toast } = useToast();
  
  // Initialize with initial media URLs
  useEffect(() => {
    if (Array.isArray(initialMediaUrls) && initialMediaUrls.length > 0) {
      console.log("Initializing with media URLs:", initialMediaUrls);
      // Filter out any non-string or empty values 
      const filteredUrls = initialMediaUrls.filter(url => url && typeof url === 'string' && url.trim() !== '');
      setPreviewMediaUrls(filteredUrls);
    } else {
      console.log("No initial media URLs provided or empty array");
      setPreviewMediaUrls([]);
    }
  }, []);
  
  // Add a new media URL
  const addMediaUrl = useCallback((url: string) => {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      console.warn("Attempted to add invalid media URL:", url);
      return;
    }
    
    console.log("Adding media URL:", url);
    setPreviewMediaUrls(prev => [...prev, url]);
    
    toast({
      title: "Image ajoutée",
      description: "L'image a été ajoutée avec succès",
    });
  }, [toast]);
  
  // Remove a media URL by index
  const removeMediaUrl = useCallback((index: number) => {
    console.log("Removing media URL at index:", index);
    setPreviewMediaUrls(prev => prev.filter((_, i) => i !== index));
    
    toast({
      title: "Image supprimée",
      description: "L'image a été supprimée avec succès",
    });
  }, [toast]);
  
  // Clean all empty URLs - useful for form submission
  const cleanEmptyUrls = useCallback(() => {
    console.log("Cleaning empty URLs");
    const filteredUrls = previewMediaUrls.filter(url => url && typeof url === 'string' && url.trim() !== '');
    
    if (filteredUrls.length !== previewMediaUrls.length) {
      console.log(`Removed ${previewMediaUrls.length - filteredUrls.length} empty URLs`);
      setPreviewMediaUrls(filteredUrls);
    }
    
    return filteredUrls;
  }, [previewMediaUrls]);
  
  // Commit all preview URLs - returns the combined URLs for saving
  const commitPreviewUrls = useCallback(() => {
    console.log("Committing preview URLs:", previewMediaUrls);
    
    // Clean and filter URLs
    const validUrls = previewMediaUrls.filter(url => url && typeof url === 'string' && url.trim() !== '');
    console.log("Valid media URLs to commit:", validUrls);
    
    return validUrls;
  }, [previewMediaUrls]);
  
  return {
    previewMediaUrls,
    addMediaUrl,
    removeMediaUrl,
    cleanEmptyUrls,
    commitPreviewUrls
  };
};

export default useMediaUrlsHandler;
